"use client";

// WO-GF-001-P1B-S35 · F226 — THE ONE MOUNTAIN CATALOGUE.
//
// FOUNDER (walk 2026-07-27): "the HUD change-mountain dropdown is poor — give it the same treatment as
// the main selector: better row separation, the F206 green/yellow status lights, US/CAN segmentation,
// scrollable with STICKY country headers exactly like F208." And the rail attached to it: "ONE source
// for rows/status/grouping shared with the main selector — no second implementation to drift."
//
// This file is that source. It is deliberately NOT a component: the two surfaces look nothing alike (a
// 268px dropdown and a two-panel modal), and forcing one component to render both is how S26's three
// divergent selectors were born the first time. What must not diverge is the FACTS —
//   • which mountains exist and in what order   (resortsActiveFirst, F125)
//   • which country each one is in              (the served `region` string, never a hardcoded list)
//   • whether it is bookable                    (the served /api/marketplace/bookable set, F184)
//   • what "bookable" and "info-only" look like (the F206 two-tier light, no red)
// — so the facts live here once and both views read them. A second VIEW is fine; a second RULESET is
// the defect. This is the same reasoning `destinationFor` already applies to the pick destination.
//
// NO INVENTED DATA, stated as a type: bookability is a three-state, not a boolean. `"unknown"` means the
// served answer has not landed (or the fetch failed) and it renders NO light at all. A boolean would
// force an unknown to paint as one of the two tiers, and an unknown painted green is a claim we cannot
// make about someone's resort.

import { useEffect, useMemo, useState } from "react";

import { mountainCodeFromPath } from "./hub-nav";

export type Country = "Canada" | "USA" | "Other";
export type MtnMeta = { slug: string; region: string | null; logoUrl: string | null };
export type Bookability = "bookable" | "info-only" | "unknown";
export type ResortRow = { href: string; label: string };

// S35 · F206 — the two status-light colours, ski-run convention. GREEN = bookable here, YELLOW = live
// but information-only. There is deliberately NO RED TIER (founder ruling): red on a run means "expert",
// and on a list of resorts it reads as "closed" or "broken", which is not a claim we can make.
// moved to @/lib/mountain-status-colors (boundary-free); re-exported so client imports are unchanged
export { STATUS_GREEN, STATUS_YELLOW } from "@/lib/mountain-status-colors";
import { STATUS_GREEN, STATUS_YELLOW } from "@/lib/mountain-status-colors";

export const COUNTRY_ORDER: readonly Country[] = ["Canada", "USA", "Other"] as const;
// Canada first is the F183 ruling ("Canada first, USA visible"), so the ORDER above is load-bearing.
export const COUNTRY_LABEL: Record<Country, string> = {
  Canada: "🇨🇦 Canada",
  USA: "🇺🇸 USA",
  Other: "Other",
};

/**
 * The country of a resort, from the SERVED `region` string. Unknown → Other, never a guess from the
 * label or the slug: "Big White" and "Big Sky" differ by one letter and two countries.
 *
 * WO-COMMERCE-BIND-001-DATA-RECONNECT · T2 — TWO SPELLINGS, BECAUSE THE SOURCE MOVED. This used to
 * read Strapi's suffix form (", CA" / ", US"). `/api/marketplace/mountains` now serves the LEGACY
 * engine's roster, whose `location.region` is the WORD ("Canada" / "USA", measured 15/15 across the
 * 30 rows). Left as it was, every mountain would have fallen through to "Other" and the selector's
 * country grouping would have quietly emptied — a regression introduced by a re-bind that "worked".
 * Both forms are accepted so the old payload keeps grading too; anything else is still Other.
 */
export function countryOf(href: string, meta: Record<string, MtnMeta>): Country {
  const code = mountainCodeFromPath(href) || "";
  const region = meta[code]?.region ?? "";
  if (/,\s*CA$/i.test(region) || /^canada$/i.test(region.trim())) return "Canada";
  if (/,\s*US$/i.test(region) || /^(usa|us|united states)$/i.test(region.trim())) return "USA";
  return "Other";
}

/** Group an ALREADY-ORDERED, already-filtered resort list into the three country buckets. Order within a
 *  bucket is the caller's (i.e. the F125 active-first rule) — this function never re-sorts. */
export function groupByCountry(rows: ResortRow[], meta: Record<string, MtnMeta>): Record<Country, ResortRow[]> {
  const out: Record<Country, ResortRow[]> = { Canada: [], USA: [], Other: [] };
  for (const r of rows) out[countryOf(r.href, meta)].push(r);
  return out;
}

/** THE three-state bookability read. `bookable === null` is "not known yet", which is not "no". */
export function bookabilityOf(code: string, bookable: Set<string> | null): Bookability {
  if (bookable === null) return "unknown";
  return bookable.has(code) ? "bookable" : "info-only";
}

/**
 * S36 card 1 · F251 — THE LEGEND VOCABULARY, RULED BY THE FOUNDER 2026-07-28:
 * "GREEN = bookable. YELLOW = information only, no booking. That is the entire legend vocabulary; use
 * exactly this meaning everywhere the lights appear (map legend, HUD dropdown, selector)."
 *
 * So it is a constant, not a string typed three times. Before this the same lamp was titled "Information
 * only" on a row and explained nowhere at all — three surfaces showed a colour and none of them said what
 * it meant, which is the whole of F251. A surface that draws a lamp reads its words from here.
 */
export const STATUS_WORDS: Record<Exclude<Bookability, "unknown">, string> = {
  bookable: "Bookable",
  "info-only": "Information only, no booking",
};

/** The F206 light. Renders NOTHING for an unknown — an absent light is honest, a coloured one is not. */
/** R3 — a surface may pass its own two lamp colours (the selector uses the homepage map's tier palette so
 *  its lamps match its pins); the FACT (which of the two a row gets) is unchanged and stays this file's. */
export type LampPalette = { green: string; yellow: string };
export function StatusLight({ status, size = 10, palette }: { status: Bookability; size?: number; palette?: LampPalette }) {
  if (status === "unknown") return null;
  const green = status === "bookable";
  const colours = palette ?? { green: STATUS_GREEN, yellow: STATUS_YELLOW };
  return (
    <span
      data-mountain-status={green ? "green" : "yellow"}
      aria-hidden
      title={STATUS_WORDS[green ? "bookable" : "info-only"]}
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: "50%",
        background: green ? colours.green : colours.yellow,
      }}
    />
  );
}

/**
 * S36 card 1 · F251 — THE LEGEND ITSELF, one component for every surface that shows a lamp.
 *
 * It renders the SAME `StatusLight` the rows render (not a look-alike swatch), so a legend can never
 * describe a colour the list does not actually use — the drift that made "what does yellow mean?" an
 * unanswerable question on three surfaces at once. `compact` is a density switch only; the WORDS are
 * `STATUS_WORDS` on every surface and there is no prop that can change them.
 */
export function StatusLegend({ compact = false, palette }: { compact?: boolean; palette?: LampPalette }) {
  const items: Exclude<Bookability, "unknown">[] = ["bookable", "info-only"];
  return (
    <div
      data-status-legend
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 10 : 14,
        flexWrap: "wrap",
        fontSize: compact ? 10 : 11,
        color: "#5b6670",
        lineHeight: 1.3,
      }}
    >
      {items.map((s) => (
        <span key={s} data-status-legend-item={s} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <StatusLight status={s} size={compact ? 8 : 9} palette={palette} />
          {STATUS_WORDS[s]}
        </span>
      ))}
    </div>
  );
}

/**
 * The served catalogue: the mountain list metadata (region + logo) and the bookability set + listing
 * counts, fetched once per opening of whichever surface mounts this.
 *
 * READINESS IS PUBLISHED, NOT GUESSED. `metaLoaded` / `bookabilityLoaded` exist because both surfaces
 * RESHAPE when these land — rows redistribute out of "Other" into Canada/USA, and a row that showed a
 * Details chevron turns into a Book action. A check that measures before that settles is measuring a
 * transient. S35 card-1b hit exactly this: `interactionstates/…-focus-ring` hovered the first Details
 * link at y=227, the bookability fetch landed, the first Details link became a different row at y=391,
 * and the check read "hover does nothing" off a build whose hover was correct (measured: the same row
 * hovered after settling reads rgb(255,245,240), the F171 tint). The fix is to state the fact in the
 * DOM so a gate can WAIT on it rather than sleep — a sleep passes today and flakes on a slower machine.
 */
export function useMountainCatalogue(active: boolean) {
  const [meta, setMeta] = useState<Record<string, MtnMeta>>({});
  const [bookable, setBookable] = useState<Set<string> | null>(null);
  const [listingCounts, setListingCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!active) return;
    let alive = true;
    fetch("/api/marketplace/mountains")
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        const map: Record<string, MtnMeta> = {};
        for (const m of j?.data ?? []) map[m.slug] = { slug: m.slug, region: m.region ?? null, logoUrl: m.logoUrl ?? null };
        setMeta(map);
      })
      .catch(() => {});
    // S31 · F184 — which mountains actually HAVE bookable product, derived at request time from the
    // marketplace listings. A failed fetch leaves this null, and null renders no light and no chip.
    fetch("/api/marketplace/bookable")
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        if (j?.ok && Array.isArray(j.mountains)) setBookable(new Set<string>(j.mountains));
        // S33 · F194 — counts are taken ONLY from an `ok` payload. A failed/degraded fetch leaves this
        // empty, so every map bubble then renders its name and no number at all.
        if (j?.ok && j.counts && typeof j.counts === "object") {
          const c: Record<string, number> = {};
          for (const [slug, n] of Object.entries(j.counts as Record<string, unknown>)) {
            if (typeof n === "number" && Number.isFinite(n)) c[slug] = n;
          }
          setListingCounts(c);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [active]);

  const metaLoaded = useMemo(() => Object.keys(meta).length > 0, [meta]);
  return { meta, bookable, listingCounts, metaLoaded, bookabilityLoaded: bookable !== null };
}
